"use client";

import {
  Button,
  ChipClose,
  ChipLabel,
  ChipRoot,
  Skeleton,
} from "@dafiti/closet-ui";
import { Trash } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";

import { useAppliedFilteredItems } from "../../../hooks/useAppliedFilteredItems.hook";
import { useFilterActions } from "../../../hooks/useFilterActions.hook";
import { useFilterParams } from "../../../hooks/useFilterParams.hook";
import { useFiltersQuery } from "../../../hooks/useFiltersQuery.hook";

export function AppliedFiltersDesktop() {
  const t = useTranslations("catalog");
  const chips = useAppliedFilteredItems();
  const { isLoading } = useFiltersQuery();
  const { removeAppliedFilter, clearAllAppliedFilters } = useFilterActions();
  const [filterParams, setFilterParams] = useFilterParams();

  const hasActiveFilters = Object.values(filterParams).some((v) => v !== null);
  const showSkeleton = isLoading && hasActiveFilters;
  const showChips = !isLoading && chips.length > 0;

  if (!showSkeleton && !showChips) return null;

  if (showSkeleton) {
    return (
      <section
        data-testid="applied-filters-desktop-skeleton"
        className="hidden lg:flex flex-wrap items-center gap-nano"
        aria-label={t("results.appliedFiltersLabel")}
      >
        <Skeleton className="h-10 w-24 rounded-full" />
        <Skeleton className="h-10 w-20 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
      </section>
    );
  }

  const handleRemove = (field: string, value: string) => {
    if (field === "price") {
      setFilterParams({ price_min: null, price_max: null });
      return;
    }
    removeAppliedFilter(field, value);
  };

  return (
    <section
      data-testid="applied-filters-desktop"
      className="hidden lg:flex flex-wrap items-center gap-nano"
      aria-label={t("results.appliedFiltersLabel")}
    >
      {chips.map((chip) => (
        <ChipRoot key={`${chip.field}-${chip.value}`} size="lg">
          <ChipLabel>{chip.label}</ChipLabel>
          <ChipClose
            onRemove={() => handleRemove(chip.field, chip.value)}
            removeAriaLabel={t("actions.removeFilter", {
              filterName: chip.label,
            })}
          />
        </ChipRoot>
      ))}
      <Button
        variant="link"
        onClick={clearAllAppliedFilters}
        size="sm"
        className="inline-flex items-center gap-1 whitespace-nowrap text-gray-600 p-2 text-body-2!"
      >
        <div className="flex h-6 w-6 items-center justify-center">
          <Trash className="size-4" />
        </div>
        {t("actions.clearFilters")}
      </Button>
    </section>
  );
}
